import { AccessRole } from "@/types/auth";

// Define role-based permissions
export enum StoreType {
  SELLER = "seller",
}

export enum Permission {
  // Coupon management
  VIEW_COUPONS = "view_coupons",
  CREATE_COUPONS = "create_coupons",
  EDIT_COUPONS = "edit_coupons",
  DELETE_COUPONS = "delete_coupons",

  // Product management
  VIEW_PRODUCTS = "view_products",
  CREATE_PRODUCTS = "create_products",
  EDIT_PRODUCTS = "edit_products",
  DELETE_PRODUCTS = "delete_products",
  BULK_UPLOAD_PRODUCTS = "bulk_upload_products",

  // Order management
  VIEW_ORDERS = "view_orders",
  EDIT_ORDERS = "edit_orders",
  PROCESS_ORDERS = "process_orders",

  // Analytics
  VIEW_ANALYTICS = "view_analytics",
  EXPORT_DATA = "export_data",

  // Store management
  EDIT_STORE_SETTINGS = "edit_store_settings",
  MANAGE_USERS = "manage_users",
}

// Permission mapping for the seller admin.
const ROLE_PERMISSIONS: Record<StoreType, Permission[]> = {
  [StoreType.SELLER]: [
    Permission.VIEW_COUPONS,
    Permission.CREATE_COUPONS,
    Permission.EDIT_COUPONS,
    Permission.DELETE_COUPONS,
    Permission.VIEW_PRODUCTS,
    Permission.CREATE_PRODUCTS,
    Permission.EDIT_PRODUCTS,
    Permission.DELETE_PRODUCTS,
    Permission.BULK_UPLOAD_PRODUCTS,
    Permission.VIEW_ORDERS,
    Permission.EDIT_ORDERS,
    Permission.PROCESS_ORDERS,
    Permission.VIEW_ANALYTICS,
    Permission.EXPORT_DATA,
    Permission.EDIT_STORE_SETTINGS,
    Permission.MANAGE_USERS,
  ],
};

// Protected routes configuration
export const PROTECTED_ROUTES: Record<string, Permission[]> = {
  "/coupons": [Permission.VIEW_COUPONS],
  "/coupons/create": [Permission.CREATE_COUPONS],
  "/coupons/edit": [Permission.EDIT_COUPONS],
  "/product/bulk-upload": [Permission.BULK_UPLOAD_PRODUCTS],
  "/analytics/export": [Permission.EXPORT_DATA],
  "/settings/users": [Permission.MANAGE_USERS],
};

/**
 * Check if a user has a specific permission
 */
export function hasPermission(
  currentStore: AccessRole | null,
  permission: Permission,
): boolean {
  if (!currentStore) return false;

  const storeType = currentStore.storeType as StoreType;
  const rolePermissions = ROLE_PERMISSIONS[storeType] || [];

  return rolePermissions.includes(permission);
}

/**
 * Check if a user can access a specific route
 */
export function canAccessRoute(
  currentStore: AccessRole | null,
  routePath: string,
): boolean {
  if (!currentStore) return false;

  // Check exact route match first
  const requiredPermissions = PROTECTED_ROUTES[routePath];
  if (requiredPermissions) {
    return requiredPermissions.every((permission) =>
      hasPermission(currentStore, permission),
    );
  }

  // Check partial route matches (for dynamic routes)
  for (const protectedRoute in PROTECTED_ROUTES) {
    if (routePath.startsWith(protectedRoute)) {
      const permissions = PROTECTED_ROUTES[protectedRoute];
      return permissions.every((permission) =>
        hasPermission(currentStore, permission),
      );
    }
  }

  // If no specific permissions required, allow access
  return true;
}

/**
 * Get user's store type safely
 */
export function getStoreType(
  currentStore: AccessRole | null,
): StoreType | null {
  if (!currentStore) return null;
  return currentStore.storeType as StoreType;
}

/**
 * Check if current store is seller
 */
export function isSeller(currentStore: AccessRole | null): boolean {
  return getStoreType(currentStore) === StoreType.SELLER;
}

/**
 * Get all permissions for current store type
 */
export function getStorePermissions(
  currentStore: AccessRole | null,
): Permission[] {
  if (!currentStore) return [];

  const storeType = currentStore.storeType as StoreType;
  return ROLE_PERMISSIONS[storeType] || [];
}

/**
 * Filter menu items based on permissions
 */
export function filterMenuByPermissions(
  menuItems: any[],
  currentStore: AccessRole | null,
): any[] {
  if (!currentStore) return [];

  return menuItems.filter((item) => {
    // Convert relative path to absolute path for checking
    let routeToCheck = item.href;
    if (routeToCheck && routeToCheck.startsWith("./")) {
      routeToCheck = routeToCheck.substring(1); // Remove the dot, keep the slash
    }

    // Check if route requires specific permissions
    if (routeToCheck && PROTECTED_ROUTES[routeToCheck]) {
      return canAccessRoute(currentStore, routeToCheck);
    }

    // Check children recursively
    if (item.children && item.children.length > 0) {
      item.children = filterMenuByPermissions(item.children, currentStore);
      return item.children.length > 0;
    }

    return true;
  });
}
